import React, { useEffect, useState } from 'react';
import Sidebar from '../../../component/b2b/sideBar/b2bSideBar';
import NavBar from '../../../component/b2b/navBar/b2bNavBar';
import Pagination from '@mui/material/Pagination';
import { useMyContext } from '../../../context/MyContext';
import { FaCcVisa, FaCcMastercard, FaDownload, FaCalendarAlt } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaFilter, FaTimes, FaCheckCircle } from "react-icons/fa";
import PaginationItem from '@mui/material/PaginationItem';
import { Base_Url, topUpList, userTrnxList } from '../../../config/config';

const B2bTopupHistory = () => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const token = localStorage.getItem('token');
  const role = localStorage.getItem('role');
  const cus_id = localStorage.getItem('cus_id');
  const [totalOrders, setTotalOrders] = useState(0);
  const { isDarkMode, isToggleSidebar } = useMyContext();
  const [historyData, setHistoryData] = useState([]);
  const [allHistoryData, setAllHistoryData] = useState([]); // Store all data for client-side filtering
  const navigate = useNavigate();

  // Add date filter states
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isDateFilterActive, setIsDateFilterActive] = useState(false);
  const [filteredData, setFilteredData] = useState([]);

  const handleNavigation = () => {
    navigate('b2b/dashboard');
  };

  const getTopupHistory = async () => {
    const apiEndpoint = role === 'admin' ? userTrnxList : topUpList;
    const url = `${Base_Url}${apiEndpoint}?page=${page}&limit=${limit}`;

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          token: token,
          cus_id: cus_id,
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      if (data.status === 'SUCCESS') {
        const transactions = data.data;

        if (isDateFilterActive) {
          // Apply client-side filtering based on dates
          const filtered = applyDateFilter(transactions);
          setHistoryData(filtered);
          setTotalOrders(filtered.length);
        } else {
          setHistoryData(transactions);
          setTotalOrders(data.pagination.totalItems || 0);
        }

        // Store all data for filtering
        setAllHistoryData(prev => {
          // Combine with previous data to build complete dataset
          const combinedData = [...prev];

          // Add new transactions if they don't already exist
          transactions.forEach(transaction => {
            if (!combinedData.some(item => item.id === transaction.id)) {
              combinedData.push(transaction);
            }
          });

          return combinedData;
        });
      } else {
        toast.error(`Error: ${data.message}`);
      }
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    }
  };

  // Function to fetch all data for export
  const fetchAllData = async () => {
    const apiEndpoint = role === 'admin' ? userTrnxList : topUpList;
    const url = `${Base_Url}${apiEndpoint}?page=1&limit=1000`; // Large limit to get as much data as possible

    try {
      toast.loading('Preparing data...');
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          token: token,
          cus_id: cus_id,
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      toast.dismiss();

      if (data.status === 'SUCCESS') {
        const allData = data.data;
        return isDateFilterActive ? applyDateFilter(allData) : allData;
      } else {
        toast.error(`Error: ${data.message}`);
        return [];
      }
    } catch (error) {
      toast.dismiss();
      toast.error(`Error: ${error.message}`);
      return [];
    }
  };

  useEffect(() => {
    getTopupHistory();
  }, [page, limit]);

  useEffect(() => {
    if (isDateFilterActive) {
      // Apply date filter to all data
      const filtered = applyDateFilter(allHistoryData);
      setFilteredData(filtered);

      // Update pagination with filtered data
      const paginatedData = filtered.slice((page - 1) * limit, page * limit);
      setHistoryData(paginatedData);
      setTotalOrders(filtered.length);
    } else {
      // Reset to normal API-based pagination
      getTopupHistory();
    }
  }, [isDateFilterActive, startDate, endDate]);

  useEffect(() => {
    if (isDateFilterActive) {
      // When page or limit changes with active filter, update displayed data
      const paginatedData = filteredData.slice((page - 1) * limit, page * limit);
      setHistoryData(paginatedData);
    }
  }, [page, limit, filteredData]);

  const applyDateFilter = (data) => {
    if (!startDate || !endDate) return data;

    const startTimestamp = new Date(startDate).setHours(0, 0, 0, 0);
    const endTimestamp = new Date(endDate).setHours(23, 59, 59, 999);

    return data.filter(item => {
      const txnDate = new Date(item.txn_date).getTime();
      return txnDate >= startTimestamp && txnDate <= endTimestamp;
    });
  };

  const handleApplyDateFilter = () => {
    if (!startDate || !endDate) {
      toast.warning('Please select both start and end dates');
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      toast.warning('Start date cannot be after end date');
      return;
    }

    setIsDateFilterActive(true);
    setPage(1); // Reset to first page when filter changes
  };

  const handleResetDateFilter = () => {
    setStartDate('');
    setEndDate('');
    setIsDateFilterActive(false);
    setPage(1);
  };

  // Function to convert JSON to CSV
  const convertToCSV = (data) => {
    const headers = role === 'admin'
      ? ['User ID', 'Transaction ID', 'Amount', 'Last Balance', 'Transaction Type', 'Transaction Status', 'Transaction Date']
      : ['Transaction ID', 'Amount', 'Last Balance', 'Transaction Type', 'Transaction Status', 'Transaction Date'];

    // Create CSV header row
    let csvContent = headers.join(',') + '\n';

    // Add data rows
    data.forEach(row => {
      const values = role === 'admin'
        ? [
          `"${row.user_id || 'N/A'}"`,
          `"${row.trnx_id || 'N/A'}"`,
          `"${row.credit_amount || 'N/A'}"`,
          `"${row.closing_balance || 'N/A'}"`,
          `"${row.payment_mode || 'N/A'}"`,
          `"${row.status === 'add' ? 'Success' : row.status || 'N/A'}"`,
          `"${row.txn_date || 'N/A'}"`,
        ]
        : [
          `"${row.trnx_id || 'N/A'}"`,
          `"${row.credit_amount || 'N/A'}"`,
          `"${row.closing_balance || 'N/A'}"`,
          `"${row.payment_mode || 'N/A'}"`,
          `"${row.status === 'add' ? 'Success' : row.status || 'N/A'}"`,
          `"${row.txn_date || 'N/A'}"`,
        ];

      csvContent += values.join(',') + '\n';
    });

    return csvContent;
  };

  const downloadTopupHistory = async () => {
    try {
      toast.loading('Preparing download...');

      // Get all data for CSV export
      const dataToExport = await fetchAllData();

      if (dataToExport.length === 0) {
        toast.dismiss();
        toast.error('No data available to download');
        return;
      }

      // Convert to CSV
      const csvContent = convertToCSV(dataToExport);

      // Create downloadable file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `topup-history-${startDate || 'all'}-to-${endDate || 'all'}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.dismiss();
      toast.success('Download successful');
    } catch (error) {
      toast.dismiss();
      toast.error(`Download failed: ${error.message}`);
    }
  };

  const renderTableHeaders = () => {
    const headers = [
      'Transaction ID',
      'Amount',
      'Last Balance',
      'Transaction Type',
      'Transaction Status',
      'Transaction Date',
      'Description',
    ];
    if (role === 'admin') headers.unshift('User ID');
    return headers;
  };

  const handlePaginationChange = (event, value) => {
    setPage(value);
  };

  const handleLimitChange = (event) => {
    setLimit(parseInt(event.target.value));
    setPage(1);
  };


  const renderTableRow = (row, index) => (
    <tr
      key={row.id}
      className={`dark:bg-black dark:text-white hover:bg-gray-100 dark:hover:bg-gray-900 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
        }`}
    >
      {/* <td className="p-3">
        <input
          type="checkbox"
          checked={selectedRows.includes(index)}
          onChange={() => handleRowSelect(index)}
          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
        />
      </td> */}
      {role === 'admin' && (
        <td className="p-2 text-xs text-black dark:text-white font-medium border-b border-gray-200 dark:border-gray-700">
          {row.user_id}
        </td>
      )}
      <td className="relative group p-2 text-xs text-black dark:text-white cursor-pointer font-semibold underline decoration-gray-300 hover:decoration-black dark:hover:decoration-white transition-colors border-b border-gray-200 dark:border-gray-700">
        {row.trnx_id || "N/A"}
        <div className="absolute left-0 top-full mt-1 hidden group-hover:block bg-black dark:bg-white text-white dark:text-black text-[10px] rounded-md px-2 py-1 shadow-lg z-10 whitespace-nowrap">
          💳 View transaction details here!
          <div className="absolute -top-1 left-3 w-1.5 h-1.5 bg-black dark:bg-white rotate-45"></div>
        </div>
      </td>
      <td className="p-2 text-xs text-black font-semibold dark:text-white border-b border-gray-200 dark:border-gray-700">
        ₹{row.credit_amount || "N/A"}
      </td>
      <td className="p-2 text-xs text-black dark:text-white border-b border-gray-200 dark:border-gray-700">
        ₹{row.closing_balance || "N/A"}
      </td>
      <td className="p-2 text-xs text-black dark:text-white capitalize border-b border-gray-200 dark:border-gray-700">
        {row.payment_mode || "N/A"}
      </td>
      <td className="p-2 border-b border-gray-200 dark:border-gray-700">
        {row.status === 'add' ? (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border bg-black text-white border-black dark:bg-white dark:text-black dark:border-white">
            Success
          </span>
        ) : (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border bg-gray-100 text-black border-gray-400 dark:bg-gray-800 dark:text-white dark:border-gray-500">
            {row.status}
          </span>
        )}
      </td>
      <td className="p-2 text-xs text-black dark:text-white border-b border-gray-200 dark:border-gray-700">
        {row.txn_date || "N/A"}
      </td>
      <td className="p-2 text-xs text-black dark:text-white max-w-xs border-b border-gray-200 dark:border-gray-700">
        <div
          className="truncate hover:text-clip hover:whitespace-normal cursor-help"
          title={row.txn_description || "No description available"}
        >
          {row.txn_description || "N/A"}
        </div>
      </td>
    </tr>
  );

  return (
    <div
      className={`h-screen flex flex-col overflow-hidden ${isDarkMode ? 'dark:bg-black' : 'bg-white'
        }`}
    >
      <div className="z-10">
        <NavBar />
      </div>

      <div className="flex flex-1 overflow-hidden">
        {isToggleSidebar && (
          <div
            className={`fixed z-20 h-full w-64 md:relative duration-300 bg-white dark:bg-black dark:text-white md:bg-transparent mt-4 md:w-64 md:z-20 border-r border-gray-200 dark:border-gray-800`}
            style={
              window.innerWidth < 768
                ? { top: '113px', height: 'calc(100vh - 115px)', overflowY: 'auto' }
                : {}
            }
          >
            <Sidebar />
          </div>
        )}

        {/* Page wrapper: fixed, no page-level scroll */}
        <div className="flex flex-col w-full h-full overflow-hidden bg-gray-200 dark:bg-black dark:text-white border border-gray-200 dark:border-gray-800 ">

          {/* Header (fixed, does not scroll) */}
          <div className="shrink-0 p-4 border-b border-gray-200 dark:border-gray-800">
            <button
              onClick={handleNavigation}
              className="text-[14px] sm:text-[14px] font-bold bg-slate-800 hover:bg-gray-800 dark:bg-white dark:text-black dark:hover:bg-gray-200 text-white font-sans rounded h-[40px] w-full flex items-center justify-center border border-black dark:border-white"
            >
              <FaCcVisa className="mr-2" size={24} />
              <FaCcMastercard className="mr-2" size={24} />
              TopUp History
            </button>

            {/* Date Picker Section */}
            <div className=" p-2 mt-2 border border-gray-200 dark:border-gray-800 rounded-lg bg-white dark:bg-black">
              <h3 className="text-[12px] font-semibold mb-1 text-black dark:text-white uppercase tracking-wide">Filter Transactions</h3>

              <div className="flex flex-col md:flex-row items-start md:items-end gap-4">
                {/* Start Date Picker */}
                <div className="flex flex-col w-full md:w-auto">
                  <label className="text-xs font-medium mb-1.5 text-black dark:text-gray-300">Start Date</label>
                  <div className="relative">
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="border border-black dark:border-white rounded-lg p-2.5 pr-9 w-full md:w-48 text-xs bg-white dark:bg-black dark:text-white focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent transition-all"
                    />
                    <FaCalendarAlt className="absolute right-3 top-1/2 -translate-y-1/2 text-black dark:text-white text-xs" />
                  </div>
                </div>

                {/* End Date Picker */}
                <div className="flex flex-col w-full md:w-auto">
                  <label className="text-xs font-medium mb-1.5 text-black dark:text-gray-300">End Date</label>
                  <div className="relative">
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="border border-black dark:border-white rounded-lg p-2.5 pr-9 w-full md:w-48 text-xs bg-white dark:bg-black dark:text-white focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent transition-all"
                    />
                    <FaCalendarAlt className="absolute right-3 top-1/2 -translate-y-1/2 text-black dark:text-white text-xs" />
                  </div>
                </div>

                {/* Buttons Group */}
                <div className="flex flex-wrap gap-2 w-full md:w-auto md:ml-auto">
                  <button
                    onClick={handleApplyDateFilter}
                    className="bg-slate-800 hover:bg-gray-800 dark:bg-white dark:text-black dark:hover:bg-gray-200 text-white font-medium rounded-lg px-4 py-2.5 text-xs flex items-center justify-center transition-colors duration-200 flex-grow md:flex-grow-0 border border-black dark:border-white"
                  >
                    <FaFilter className="mr-2" />
                    Apply Filter
                  </button>

                  {isDateFilterActive && (
                    <button
                      onClick={handleResetDateFilter}
                      className="bg-white hover:bg-gray-100 text-black dark:bg-black dark:text-white dark:hover:bg-gray-900 font-medium rounded-lg px-4 py-2.5 text-xs flex items-center justify-center transition-colors duration-200 flex-grow md:flex-grow-0 border border-black dark:border-white"
                    >
                      <FaTimes className="mr-2" />
                      Reset
                    </button>
                  )}

                  <button
                    onClick={downloadTopupHistory}
                    className="bg-white hover:bg-gray-100 text-black dark:bg-black dark:text-white dark:hover:bg-gray-900 font-medium rounded-lg px-4 py-2.5 text-xs flex items-center justify-center transition-colors duration-200 flex-grow md:flex-grow-0 border border-black dark:border-white"
                  >
                    <FaDownload className="mr-2" />
                    Download
                  </button>
                </div>
              </div>

              {/* Filter Status Message */}
              {isDateFilterActive && (
                <div className="mt-3 text-xs font-medium flex items-center text-black dark:text-white">
                  <FaCheckCircle className="mr-2" />
                  Showing data from {new Date(startDate).toLocaleDateString()} to {new Date(endDate).toLocaleDateString()}
                </div>
              )}
            </div>
          </div>

          {/* Table area: ONLY this part scrolls */}
          <div className="flex-1 overflow-auto custom-scrollbar px-4">
            <div className="overflow-x-auto rounded-md border border-gray-200 dark:border-gray-800">
              <table className="min-w-full table-auto dark:bg-black dark:text-white">
                <thead className='bg-slate-800 text-white text-[10px] dark:bg-white dark:text-black sticky top-0 z-10'>
                  <tr>
                    {/* <th className="p-2 text-left font-medium">
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                        className="w-4 h-4 ml-1 text-blue-600 bg-white border-gray-300 rounded focus:ring-blue-500"
                      />
                    </th> */}
                    {renderTableHeaders().map((header, index) => (
                      <th key={index} className="p-2 text-left font-medium">
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-800 dark:bg-black dark:text-white">
                  {historyData?.length > 0 ? (
                    historyData.map((row, index) => renderTableRow(row, index))
                  ) : (
                    <tr>
                      <td colSpan={role === 'admin' ? "9" : "8"} className="text-center p-4 text-gray-500 dark:text-gray-400">
                        {isDateFilterActive ? 'No data available for the selected date range' : 'No data available'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Footer / pagination (fixed, does not scroll) */}
          <div className="shrink-0 p-2 border-t border-gray-200 dark:border-gray-800 flex flex-col sm:flex-row justify-between items-center space-y-2 sm:space-y-0">
            <span className="text-xs text-black font-bold dark:text-gray-200">{`Total : ${totalOrders}`}</span>
            <select
              id="limit"
              value={limit}
              onChange={handleLimitChange}
              className="border border-black dark:border-white rounded p-1 text-xs bg-white dark:bg-black dark:text-white"
            >
              {totalOrders === 0 ? (
                <option value={0}>0</option>
              ) : (
                <>
                  {totalOrders >= 10 && <option value={10}>10</option>}
                  {totalOrders >= 15 && <option value={15}>15</option>}
                  {totalOrders >= 20 && <option value={20}>20</option>}
                  {totalOrders >= 30 && <option value={30}>30</option>}
                  {totalOrders >= 30 && <option value={totalOrders}>All</option>}
                </>
              )}
            </select>

            <Pagination
              count={Math.ceil(totalOrders / limit)}
              page={page}
              onChange={handlePaginationChange}
              variant="outlined"
              shape="rounded"
              className="pagination"
              size="small"
              renderItem={(item) => (
                <PaginationItem
                  {...item}
                  className={`mx-1 rounded-md ${item.selected ? 'bg-black text-white dark:bg-white dark:text-black' : 'text-black dark:text-gray-300'} hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black`}
                />
              )}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
export default B2bTopupHistory;